import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import '../style/common.css';
import './RAAfterAprilPage.css';

const RAAfterAprilPage = () => {
  const [bills, setBills] = useState([]);
  const [jcList, setJCList] = useState([]);
  const [assignedList, setAssignedList] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [selectedJC, setSelectedJC] = useState('');
  const [selectedAssigned, setSelectedAssigned] = useState('');
  const [summary, setSummary] = useState({
    totalJCs: 0,
    totalBills: 0,
    totalAmount: 0,
    latestBillDate: '-',
    totalPOs: 0,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      const userSnap = await getDocs(collection(db, 'users'));
      const map = {};
      userSnap.forEach((doc) => {
        const data = doc.data();
        map[doc.id] = data.name || data.email || 'Unnamed';
      });
      setUserMap(map);
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchBills = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const startDateStr = `${currentYear}-04-01`;

        const raQuery = query(
          collection(db, 'ra_bills'),
          where('bill_date', '>=', startDateStr)
        );

        const raSnap = await getDocs(raQuery);
        const allBills = [];
        const jcSet = new Set();
        const assignedSet = new Set();
        let totalAmount = 0;
        let latestBillDate = null;
        const uniquePOs = new Set();

        for (const docSnap of raSnap.docs) {
          const bill = docSnap.data();
          if (!bill.po_id) continue;

          const poRef = await getDoc(doc(db, 'purchase_orders', bill.po_id));
          if (!poRef.exists()) continue;

          const poData = poRef.data();
          const jc = poData.jc_number || 'Unknown JC';
          const assignedUID = poData.assigned_to || 'Unassigned';
          const assignedName = userMap[assignedUID] || assignedUID;

          const combinedBill = {
            ...bill,
            po: poData,
            jc,
            assigned_to: assignedName,
            po_id: bill.po_id,
            po_number: poData.po_number || '-',
            description: poData.description || '-',
          };

          allBills.push(combinedBill);
          jcSet.add(jc);
          assignedSet.add(assignedName);
          totalAmount += parseFloat(bill.amount || 0);
          uniquePOs.add(bill.po_id);

          if (bill.bill_date) {
            const billDateObj = new Date(bill.bill_date);
            if (!latestBillDate || billDateObj > latestBillDate) {
              latestBillDate = billDateObj;
            }
          }
        }

        allBills.sort((a, b) => new Date(b.bill_date) - new Date(a.bill_date)).reverse();

        setBills(allBills);
        setJCList(Array.from(jcSet).sort());
        setAssignedList(Array.from(assignedSet).sort());
        setSummary({
          totalJCs: jcSet.size,
          totalBills: allBills.length,
          totalAmount,
          latestBillDate: latestBillDate
            ? latestBillDate.toLocaleDateString('en-IN')
            : '-',
          totalPOs: uniquePOs.size,
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching RA Bills:', err);
        setLoading(false);
      }
    };

    if (Object.keys(userMap).length > 0) {
      fetchBills();
    }
  }, [userMap]);

  const handleRowClick = (poId) => {
    navigate(`/admin/po-detail/${poId}`);
  };

  const filteredBills = bills.filter((bill) => {
    return (
      (!selectedJC || bill.jc === selectedJC) &&
      (!selectedAssigned || bill.assigned_to === selectedAssigned)
    );
  });

  const dynamicSummary = {
    totalJCs: new Set(filteredBills.map((b) => b.jc)).size,
    totalBills: filteredBills.length,
    totalAmount: filteredBills.reduce((sum, b) => sum + parseFloat(b.amount || 0), 0),
    latestBillDate: (() => {
      const dates = filteredBills.map((b) => new Date(b.bill_date)).filter((d) => !isNaN(d));
      if (dates.length === 0) return '-';
      return new Date(Math.max(...dates)).toLocaleDateString('en-IN');
    })(),
    totalPOs: new Set(filteredBills.map((b) => b.po_id)).size,
  };

  return (
    <div className="ra-after-april-wrapper">
      <h2>RA Bills Applied After 1st April</h2>

      {!loading && (
        <>
          <div className="summary-tab">
            <p><strong>Total JC Count:</strong> {dynamicSummary.totalJCs}</p>
            <p><strong>Total Bill Count:</strong> {dynamicSummary.totalBills}</p>
            <p><strong>Grand Total Amount:</strong> ₹{dynamicSummary.totalAmount.toLocaleString('en-IN')}</p>
            <p><strong>Latest Bill Date:</strong> {dynamicSummary.latestBillDate}</p>
          </div>

          <div className="filters">
            <label>
              Filter by JC:
              <select value={selectedJC} onChange={(e) => setSelectedJC(e.target.value)}>
                <option value="">All JCs</option>
                {jcList.map((jc, idx) => (
                  <option key={idx} value={jc}>{jc}</option>
                ))}
              </select>
            </label>

            <label>
              Filter by Assigned To:
              <select value={selectedAssigned} onChange={(e) => setSelectedAssigned(e.target.value)}>
                <option value="">All Assigned</option>
                {assignedList.map((name, idx) => (
                  <option key={idx} value={name}>{name}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="flat-table">
            {filteredBills.length === 0 ? (
              <p>No RA Bills match selected filters.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>JC</th>
                    <th>Assigned To</th>
                    <th>Bill Name</th>
                    <th>Invoice Number</th>
                    <th>Amount (₹)</th>
                    <th>Bill Date</th>
                    <th>PO Number</th>
                    <th>Work Description</th>
                    <th>PO Location</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBills.map((bill, idx) => (
                    <tr
                      key={idx}
                      className="clickable-row"
                      onClick={() => handleRowClick(bill.po_id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{bill.jc}</td>
                      <td>{bill.assigned_to}</td>
                      <td>{bill.bill_name || '-'}</td>
                      <td>{bill.bill_number || '-'}</td>
                      <td>{Number(bill.amount || 0).toLocaleString('en-IN')}</td>
                      <td>{bill.bill_date || '-'}</td>
                      <td>{bill.po_number}</td>
                      <td>{bill.description}</td>
                      <td>{bill.po?.location || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {loading && <p>Loading...</p>}
    </div>
  );
};

export default RAAfterAprilPage;
